import React from "react";
import { Box, Typography } from "@mui/material";

// ---------- Extraction helpers ----------

// Match a standout numeric figure: $50B, $50-56 billion, 38.9%, 1,250+, 3.2x
const FIGURE_REGEX =
  /(\$\s?\d+(?:[.,]\d+)?(?:\s?[–—-]\s?\d+(?:[.,]\d+)?)?\s?(?:trillion|billion|million|thousand|[TBMK])?\b)|(\d+(?:\.\d+)?\s?%)|(\d{1,3}(?:,\d{3})+\+?)|(\d+(?:\.\d+)?\s?x\b)|(\d{3,}\+)/i;

// Remove markdown emphasis / links so UI text reads cleanly.
const stripMarkdown = (s) =>
  String(s || "")
    .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1") // [text](url) -> text
    .replace(/`([^`]+)`/g, "$1")
    .replace(/\*\*([^*]+)\*\*/g, "$1")
    .replace(/\*([^*]+)\*/g, "$1")
    .replace(/_([^_]+)_/g, "$1")
    .replace(/\s+/g, " ")
    .trim();

/**
 * Extract KPI tiles from bold-lead paragraphs like:
 *   "**Market size & growth.** The global AI ... reach $50–56 billion in 2026 ..."
 * Returns up to 6 { title, value, sub } entries.
 */
export const extractKpis = (markdown) => {
  if (!markdown) return [];
  const text = String(markdown);

  // Bold lead + sentence
  const leadRegex = /\*\*([^*\n]{2,80})\*\*\s*[.:]\s+([^\n]{10,400})/g;
  const kpis = [];
  const seen = new Set();
  let m;

  while ((m = leadRegex.exec(text)) !== null && kpis.length < 6) {
    const title = stripMarkdown(m[1]).replace(/[.:]+$/, "").trim();
    const sentence = stripMarkdown(m[2]);
    const fig = sentence.match(FIGURE_REGEX);
    if (!fig) continue;

    const value = fig[0].replace(/\s+/g, " ").trim();
    const key = title.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);

    // Subtitle: short contextual phrase around the figure
    const subRaw = sentence
      .replace(FIGURE_REGEX, "")
      .replace(/\s+/g, " ")
      .trim()
      .replace(/^[,.;:\s]+/, "");
    const subWords = subRaw.split(/\s+/).slice(0, 10).join(" ");
    const sub = subWords.replace(/[.,;:]+$/, "");

    kpis.push({ title, value, sub });
  }
  return kpis;
};

/**
 * Detect two adjacent same-level headings whose body is ONLY a bulleted list.
 * Returns { leftTitle, leftItems, rightTitle, rightItems } or null.
 */
export const extractPairedLists = (markdown) => {
  if (!markdown) return null;
  const lines = String(markdown).split("\n");
  const headings = [];
  for (let i = 0; i < lines.length; i++) {
    const hm = lines[i].match(/^(#{2,3})\s+(.+?)\s*$/);
    if (hm) headings.push({ line: i, level: hm[1].length, title: hm[2].trim() });
  }
  const isBullets = (arr) =>
    arr.length >= 2 && arr.every((l) => /^[-*•]\s+/.test(l));
  const clean = (arr) =>
    arr
      .map((l) => l.trim())
      .filter(Boolean);

  for (let k = 0; k + 1 < headings.length; k++) {
    const a = headings[k];
    const b = headings[k + 1];
    if (a.level !== b.level) continue;

    const aBody = clean(lines.slice(a.line + 1, b.line));
    const nextH = headings[k + 2]?.line ?? lines.length;
    const bBody = clean(lines.slice(b.line + 1, nextH));
    if (!isBullets(aBody) || !isBullets(bBody)) continue;

    return {
      leftTitle: a.title.replace(/\*\*/g, "").trim(),
      leftItems: aBody.map((l) => stripMarkdown(l.replace(/^[-*•]\s+/, ""))),
      rightTitle: b.title.replace(/\*\*/g, "").trim(),
      rightItems: bBody.map((l) => stripMarkdown(l.replace(/^[-*•]\s+/, ""))),
    };
  }
  return null;
};

// ---------- UI ----------

const KpiTile = ({ title, value, sub }) => (
  <Box
    sx={{
      backgroundColor: "#f4f4f5",
      borderRadius: "12px",
      p: 1.75,
      display: "flex",
      flexDirection: "column",
      gap: 0.5,
      minHeight: 96,
      transition: "background-color 0.2s ease, transform 0.2s ease",
      "&:hover": { backgroundColor: "#eeeef0" },
    }}
  >
    <Typography
      sx={{
        fontSize: "0.75rem",
        color: "#6b7280",
        fontWeight: 500,
        lineHeight: 1.3,
      }}
    >
      {title}
    </Typography>
    <Typography
      sx={{
        fontSize: "1.375rem",
        fontWeight: 700,
        color: "#1a1a2e",
        lineHeight: 1.15,
        letterSpacing: "-0.01em",
      }}
    >
      {value}
    </Typography>
    {sub && (
      <Typography
        sx={{
          fontSize: "0.75rem",
          color: "#6b7280",
          lineHeight: 1.45,
        }}
      >
        {sub}
      </Typography>
    )}
  </Box>
);

const ListCard = ({ title, items }) => (
  <Box
    sx={{
      border: "1px solid #e5e7eb",
      borderRadius: "12px",
      p: 2,
      backgroundColor: "#ffffff",
    }}
  >
    <Typography
      sx={{
        fontSize: "0.9375rem",
        fontWeight: 600,
        color: "#1a1a2e",
        mb: 1,
      }}
    >
      {title}
    </Typography>
    <Box
      component="ul"
      sx={{
        pl: 2,
        m: 0,
        display: "flex",
        flexDirection: "column",
        gap: 0.75,
      }}
    >
      {items.map((it, i) => (
        <Box
          component="li"
          key={i}
          sx={{
            fontSize: "0.8125rem",
            color: "#374151",
            lineHeight: 1.55,
          }}
        >
          {it}
        </Box>
      ))}
    </Box>
  </Box>
);

const SectionLabel = ({ children }) => (
  <Typography
    sx={{
      fontSize: "0.6875rem",
      fontWeight: 600,
      letterSpacing: "0.08em",
      textTransform: "uppercase",
      color: "#6b7280",
      mb: 1,
    }}
  >
    {children}
  </Typography>
);

/**
 * Renders a visual overview (KPI tiles + paired-list cards) derived from a
 * markdown answer. If nothing useful can be extracted, renders null so the
 * markdown below is the sole content.
 */
const VisualSummary = ({ markdown }) => {
  const kpis = extractKpis(markdown);
  const paired = extractPairedLists(markdown);

  if (kpis.length < 2 && !paired) return null;

  const kpiCols =
    kpis.length >= 4
      ? "repeat(4, minmax(0, 1fr))"
      : `repeat(${Math.max(kpis.length, 1)}, minmax(0, 1fr))`;

  return (
    <Box sx={{ mb: 2.5 }}>
      {kpis.length >= 2 && (
        <Box sx={{ mb: paired ? 2 : 0 }}>
          <SectionLabel>Snapshot</SectionLabel>
          <Box
            sx={{
              display: "grid",
              gap: 1.25,
              gridTemplateColumns: {
                xs: "repeat(2, minmax(0, 1fr))",
                sm: kpiCols,
              },
            }}
          >
            {kpis.map((k, i) => (
              <KpiTile key={i} {...k} />
            ))}
          </Box>
        </Box>
      )}

      {paired && (
        <Box
          sx={{
            display: "grid",
            gap: 1.5,
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
          }}
        >
          <ListCard title={paired.leftTitle} items={paired.leftItems} />
          <ListCard title={paired.rightTitle} items={paired.rightItems} />
        </Box>
      )}
    </Box>
  );
};

export default VisualSummary;
